import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldAlert, UserPlus, Search as SearchIcon, MessageCircle, Plus } from "lucide-react";
import HeroSearch from "@/components/search/HeroSearch";
import PropertyGrid from "@/components/property/PropertyGrid";
import { Empty } from "@/components/common/States";
import Seo from "@/components/layout/Seo";
import { useI18n } from "@/i18n/I18nProvider";
import { useTheme } from "@/context/Theme";
import { useLocations } from "@/hooks/useData";
import { api } from "@/lib/api";

function Section({ title, to, children, testId }) {
  const { t } = useI18n();
  return (
    <section data-testid={testId} className="mx-auto max-w-7xl px-4 mt-16">
      <div className="flex items-end justify-between mb-6">
        <h2 className="section-title">{title}</h2>
        {to && <Link to={to} className="text-sm font-semibold text-gold hover:text-gold-hover">{t("common.viewAll")}</Link>}
      </div>
      {children}
    </section>
  );
}

function Governorates({ counts }) {
  const { t, nm } = useI18n();
  const { items } = useLocations(null);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="governorates-grid">
      {items.map((g) => (
        <Link key={g.id} to={`/search?governorate_id=${g.id}`} data-testid={`gov-link-${g.id}`}
          className="group rounded-xl border border-line bg-white px-4 py-3 hover:border-gold hover:bg-sand transition-colors">
          <span className="block font-display font-semibold text-navy group-hover:text-gold-hover">{nm(g)}</span>
          <span className="text-xs text-ink-muted">{counts?.[g.id] || 0} {t("home.listings")}</span>
        </Link>
      ))}
    </div>
  );
}

const HOW_ICONS = [UserPlus, SearchIcon, MessageCircle];

function HeroMap() {
  const { theme } = useTheme();
  return (
    <div aria-hidden="true" data-testid="hero-yemen-map" className="hero-map pointer-events-none select-none absolute inset-0 flex items-start lg:items-center justify-center overflow-hidden">
      <img src={`/yemen-map-${theme === "dark" ? "dark" : "light"}.svg`} alt="" fetchPriority="high" width="1012" height="574"
        className="shrink-0 w-[150%] sm:w-[110%] lg:w-[54rem] max-w-none h-auto mt-8 lg:mt-0" />
    </div>
  );
}

export default function Home() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [wanted, setWanted] = useState([]);
  const [offices, setOffices] = useState([]);
  useEffect(() => {
    api.get("/properties/home").then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/wanted", { params: { limit: 6 } }).then((r) => setWanted(r.data.items)).catch(() => {});
    api.get("/agencies", { params: { verified: true, limit: 6 } }).then((r) => setOffices(r.data.items)).catch(() => {});
  }, []);
  const loading = data === null;
  const addCta = <Link to="/add-property" className="btn-gold" data-testid="empty-add-property-btn"><Plus className="w-4 h-4" />{t("nav.add")}</Link>;
  const grid = (key) => <PropertyGrid items={data?.[key]} loading={loading} testId={`home-${key}`} emptyTitle={t("home.noListings")} emptyHint={t("home.noListingsHint")} emptyAction={addCta} />;

  return (
    <div>
      <Seo />
      <section className="hero-bg relative overflow-hidden" data-testid="hero-section">
        <HeroMap />
        <div className="relative mx-auto max-w-4xl px-4 pt-10 pb-12 sm:pt-16 sm:pb-16 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 data-testid="hero-title" aria-label={t("hero.title")} className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight text-navy">
              {t("hero.title").split(" — ").map((part, i) => <span key={i} className={`block ${i ? "text-gold mt-1 text-3xl sm:text-4xl lg:text-6xl" : ""}`}>{part}</span>)}
            </h1>
            <p data-testid="hero-subtitle" className="mt-5 text-base sm:text-lg text-navy/80 dark:text-white/85 max-w-xl mx-auto">{t("hero.subtitle")}</p>
          </motion.div>
          <div className="mt-8 sm:mt-10 max-w-3xl mx-auto"><HeroSearch /></div>
        </div>
      </section>

      <Section title={t("home.latest")} to="/search" testId="section-latest">{grid("latest")}</Section>
      {data?.featured?.length > 0 && <Section title={t("home.featured")} testId="section-featured">{grid("featured")}</Section>}
      <Section title={t("home.byGov")} testId="section-governorates"><Governorates counts={data?.governorate_counts} /></Section>
      <Section title={t("home.residential")} to="/search?category=residential" testId="section-residential">{grid("residential")}</Section>
      <Section title={t("home.land")} to="/search?category=land&purpose=sale" testId="section-land">{grid("land")}</Section>
      <Section title={t("home.commercial")} to="/search?category=commercial" testId="section-commercial">{grid("commercial")}</Section>
      <Section title={t("home.wanted")} to="/wanted" testId="section-wanted">
        {wanted.length ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{wanted.map((w) => <div key={w.id} className="rounded-xl border border-line p-4"><p className="font-bold text-navy">{w.title}</p><p className="text-xs font-mono text-ink-muted">{w.ref}</p></div>)}</div> : <Empty title={t("home.noWanted")} testId="wanted-empty" />}
      </Section>
      <Section title={t("home.offices")} to="/offices" testId="section-offices">
        {offices.length ? <div className="grid gap-3 sm:grid-cols-3">{offices.map((o) => <div key={o.id} className="rounded-xl border border-line p-4 font-bold text-navy">{o.name}</div>)}</div> : <Empty title={t("home.noOffices")} testId="offices-empty" />}
      </Section>

      <Section title={t("home.how")} testId="section-how">
        <div className="grid gap-5 md:grid-cols-3">
          {t("home.howSteps").map(([title, body], i) => {
            const Icon = HOW_ICONS[i];
            return (
              <div key={title} className="relative rounded-2xl bg-sand p-6 border border-line">
                <span className="absolute top-5 end-5 font-display text-4xl font-bold text-gold/25">{i + 1}</span>
                <Icon className="w-8 h-8 text-gold" />
                <h3 className="mt-4 font-bold text-navy">{title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">{body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title={t("home.safety")} to="/page/safety" testId="section-safety">
        <div className="rounded-2xl bg-navy text-white p-6 sm:p-8 grid gap-4 md:grid-cols-2">
          {t("home.tips").map((tip) => (
            <p key={tip} className="flex gap-3 text-sm leading-relaxed"><ShieldAlert className="w-5 h-5 shrink-0 text-gold-light" />{tip}</p>
          ))}
        </div>
      </Section>
    </div>
  );
}
